import { useEffect, useRef } from 'react';
import { ErrorResponse } from 'oidc-client-ts';
import { IDENTITY_REQUEST_TIMEOUT_SECONDS } from 'features/auth/constants';
import { identityErrorCause } from 'features/auth/utils/identityErrorCause';
import { logger } from 'features/logger/logger';
import type { UserManagerEvents } from 'oidc-client-ts';
import type { ErrorContext } from 'react-oidc-context';

export type TokenRenewalAuth = {
  events: Pick<UserManagerEvents, 'addAccessTokenExpiring'>;
  signinSilent: (args: {
    silentRequestTimeoutInSeconds: number;
  }) => Promise<unknown>;
  error?: ErrorContext | undefined;
};

// The refresh-token request fails with invalid_grant once Identity has ended
// the session; every other code leaves the session in place.
const SESSION_ENDED_ERROR = 'invalid_grant';

const identityErrorCode = (error: ErrorContext): string | null =>
  error.innerError instanceof ErrorResponse ? error.innerError.error : null;

// Renewal is a refresh-token exchange that resets Identity's idle clock, so
// it runs only while the participant is not idle.
export const useActivityGatedTokenRenewal = (
  auth: TokenRenewalAuth,
  isIdle: () => boolean,
  enabled: boolean,
  onSessionEnded: () => void
): void => {
  const { events, signinSilent, error } = auth;
  const renewalPending = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return events.addAccessTokenExpiring(() => {
      if (isIdle()) {
        logger.debug('Access token expiring while idle, not renewing');
        return;
      }

      renewalPending.current = true;
      // The refresh-token request takes its timeout from this argument only.
      void signinSilent({
        silentRequestTimeoutInSeconds: IDENTITY_REQUEST_TIMEOUT_SECONDS,
      }).then(user => {
        if (user !== null) {
          renewalPending.current = false;
        }
      });
    });
  }, [enabled, events, isIdle, signinSilent]);

  useEffect(() => {
    if (!renewalPending.current || error?.source !== 'signinSilent') {
      return;
    }

    renewalPending.current = false;
    const code = identityErrorCode(error);

    if (code === SESSION_ENDED_ERROR) {
      logger.info('Identity rejected the token renewal; logging out', {
        details: { error: code },
      });
      onSessionEnded();
      return;
    }

    const cause = identityErrorCause(error);
    logger.warn(
      'Silent token renewal failed',
      { details: { error: error.message, name: cause?.name, code } },
      cause
    );
  }, [error, onSessionEnded]);
};
